// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the Play surface (MVP WS2): which model constants become
// sliders ("levers"), and what numeric range each slider spans. Pure and
// engine-agnostic so it unit-tests without a live model or WASM. The imperative
// shell (PlayPanel component + Editor wiring) supplies the variables and applies
// the overrides via the engine's Model.run(overrides).

/** A model constant that becomes a Play slider (a tweakable "lever"). */
export interface PlayParameter {
  /** Canonical variable identifier (the key Model.run overrides are keyed by). */
  ident: string;
  /** Display label. */
  name: string;
  /** The model's current constant value. */
  value: number;
}

/** A minimal, engine-agnostic view of a model variable. */
export interface VariableLike {
  ident: string;
  type: string;
  equation?: string;
}

/**
 * If `equation` is a bare numeric literal (a constant), return its value;
 * otherwise undefined. Anything with an operator or reference (`a + b`, `1/4`,
 * `time`) is not a slider-able constant.
 */
export function constantValue(equation: string | undefined): number | undefined {
  if (equation === undefined) {
    return undefined;
  }
  const trimmed = equation.trim();
  if (!/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(trimmed)) {
    return undefined;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Extract the tweakable parameters -- auxiliaries whose equation is a constant --
 * from a model's variables, sorted by name. These are the Play sliders. Stocks
 * (initial values) and flows (rate equations) are intentionally excluded: the MVP
 * lever set is the classic constant auxiliaries.
 */
export function modelParameters(variables: Iterable<VariableLike>): PlayParameter[] {
  const params: PlayParameter[] = [];
  for (const v of variables) {
    if (v.type !== 'aux') {
      continue;
    }
    const value = constantValue(v.equation);
    if (value === undefined) {
      continue;
    }
    params.push({ ident: v.ident, name: v.ident, value });
  }
  params.sort((a, b) => a.name.localeCompare(b.name));
  return params;
}

export interface SliderRange {
  min: number;
  max: number;
  step: number;
}

// ~100 steps across the range, snapped to a power of ten so the slider lands on
// readable values.
function niceStep(span: number): number {
  if (!(span > 0)) {
    return 0.01;
  }
  return Math.pow(10, Math.floor(Math.log10(span / 100)));
}

/**
 * Derive a sensible slider range from a constant's current value: 0..2x for a
 * positive value (so it can go to zero or double), 2x..0 for a negative value,
 * and a small symmetric range around zero. The current value therefore always
 * sits inside the range and can move meaningfully in both directions.
 */
export function sliderRange(value: number): SliderRange {
  if (!Number.isFinite(value) || value === 0) {
    return { min: -1, max: 1, step: 0.01 };
  }
  const min = value > 0 ? 0 : 2 * value;
  const max = value > 0 ? 2 * value : 0;
  return { min, max, step: niceStep(max - min) };
}
